(ns nosco.ui.components
  (:require
   ["react" :as React]
   [clojure.string :as str]
   [goog.dom.classlist]
   [goog.object]
   [goog.string]
   [goog.style]
   [hx.hooks :refer [useContext useEffect useLayoutEffect useState] :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.colors :as colors]
   [nosco.i18n.translate :as t]
   [nosco.icons :as icons :refer [svg-icon]]
   [nosco.icons2 :as icons2]
   [nosco.images :as images]
   [nosco.react]
   [nosco.router :as router]
   [nosco.ui.html]
   [nosco.ui.pills :as pills]
   [nosco.ui.popup :as popup]
   [nosco.ui.text :as text]
   [nosco.util :as util]
   [nosco.util.format :as nosco-format]
   [shadow.resource]))

(defnc EmptyState [{:keys [title image body button]}]
  [:div {:class "d-flex align-items-center flex-column"}
   [:div
    [:img {:src image}]]
   [:h1 {:class "text-gray-500"} title]
   (when body [:p body])
   (when button [:div button])])

(defnc InlineColoredDot [{:keys [size color] :as props
                          :or {size "0.25rem"}}]
  [:span (nosco.react/merge-props
          {:style {:width size
                   :height size
                   :vertical-align "middle"
                   :border-radius "9999px"
                   :background-color color
                   :display "inline-block"}}
          (dissoc props :size :color)) "\u00A0"])

(defnc Circle [{:keys [size] :as props}]
  [:div (nosco.react/merge-props
         {:style {:width size
                  :height size
                  :border-radius "9999px"}}
         props)])

;; ShowMoreButton should be used for fetching more items from server for a list.
(defnc ShowMoreButton [{:keys [on-click is-loading class] :or {class "w-50"}}]
  [:div {:class "text-center p-3"}
   [:button
    {:on-click on-click
     :class    ["btn btn-dark btn-block rounded-pill d-inline-block" class]}
    (if is-loading
      [:div {:class "spinner-grow text-light"
             :role  "status"}
       [:span {:class "sr-only"} (t/t "Loading...")]]
      (t/t "Show more"))]])

;; A search input box that takes placeholder text and an on change fn.
(defnc SearchInputBox
  [{:keys [placeholder-text on-change-fn debounce-time class default-value loading? autofocus]
    :or {debounce-time 400
         placeholder-text (t/t "Search...")
         default-value ""}}]
  (let [[search ->search]        (useState default-value)
        [spinning? set-spinning] (useState false)
        debounced-fn             (util/use-debounce debounce-time (fn [x]
                                                                    (on-change-fn x)
                                                                    (set-spinning false)))]
    [:div
     {:class ["d-flex position-relative justify-content-center align-items-center" class]}
     [:input {:class       "form-control rounded-pill"
              :type        "text"
              :placeholder placeholder-text
              :autoFocus   autofocus
              :value       search
              :on-change   #(let [value (-> % .-target .-value)]
                              (set-spinning true)
                              (->search value)
                              (debounced-fn value))}]
     [:div
      [:div {:style {:opacity (if (or spinning? loading?) 1 0)
                     :margin "auto"
                     :top 0
                     :bottom 0
                     :right "0.75em"
                     :pointer-events "none"}
             :class ["position-absolute spinner-border spinner-border-sm text-secondary"]}]]]))

(defnc SearchInputBox2
  [{:keys [placeholder-text value on-change-fn loading? class]
    :or {placeholder-text (t/t "Search...")}}]
  [:div
   {:class ["d-flex position-relative justify-content-center align-items-center" class]}
   [:input {:class       "form-control rounded-pill"
            :type        "text"
            :placeholder placeholder-text
            :value       (str value)
            :on-change   #(let [value (-> % .-target .-value)]
                            (on-change-fn value))}]
   [:div {:style {:opacity (if loading? 1 0)
                  :margin "auto"
                  :top 0 :bottom 0 :right "0.75rem"}
          :class ["position-absolute spinner-border spinner-border-sm text-secondary"]}]])

;; Use this button as a "submit" button for saving or creating items. There can only be one Primary Button on a page.
(defnc PrimaryButton
  [{:keys [loading? on-click icon title loading-title class disabled]
    :or {class "btn-primary"}}
   ref]
  [:button {:type "button"
            :disabled disabled
            :ref ref
            :class ["btn btn-lg shadow-sm" class icon (when-not title "icon-only")]
            :on-click on-click}
   (when icon (svg-icon icon)) ;; The icon
   (if loading?
     [:span {:class "spinner-grow spinner-grow-sm"
             :role "status"
             :aria-hidden "true"}
      [:span {:class "sr-only"} loading-title]]
     title)])

(nosco.react/forward-ref! PrimaryButton)

(defnc BaseActionButton
  [{:keys [type loading? on-click icon title loading-title class disabled form]
    :or {type "button"
         on-click identity}}
   ref]
  [:button {:type type
            :disabled disabled
            :ref ref
            :form form
            :class [class (when-not title "icon-only")]
            :on-click on-click}
   (when icon (svg-icon icon)) ;; The icon
   (if loading?
     [:span {:class "spinner-grow spinner-grow-sm"
             :role "status"
             :aria-hidden "true"}
      [:span {:class "sr-only"} loading-title]]
     title)])

(nosco.react/forward-ref! BaseActionButton)

(defnc ActionButton
  [{:keys [class]
    :or {class "btn-outline-primary"}
    :as props}
   ref]
  [BaseActionButton (assoc props
                           :ref ref
                           :class (str "btn " class))])

(nosco.react/forward-ref! ActionButton)

(defnc Spacer [{:keys [class]
                :or {class "mr-2"}}]
  [:div {:class class}])

(defnc ButtonGroup [{:keys [class children]
                     :or {class ""}}]
  [:div {:class ["btn-group" class]}
   children])

(defnc InlineActionButton [{:keys [btn-class children icon showing set-showing]
                            :or {btn-class "btn btn-link btn-sm icon-only"}}]
  (let [showing-hook                     (React/useState false)
        [showing set-showing]            (if (and (nil? showing) (nil? set-showing))
                                           showing-hook
                                           [showing set-showing])
        popup-ref                        (React/useRef nil)
        button-ref                       (React/useRef nil)
        close-menu                       #(set-showing false)
        [button-ref-cb popper] (popup/use-popper {:placement "bottom"})]
    (popup/use-click-outside showing close-menu button-ref popup-ref)
    (popup/use-escape showing close-menu)
    [:<>
     [:button {:class    btn-class
               :on-click (fn [e]
                           (.preventDefault e)
                           (set-showing not))
               :ref      (nosco.react/merge-refs button-ref button-ref-cb)}
      (when icon
        (svg-icon icon))]
     [popup/Overlay {:ref popup-ref
                     :class "nosco-overlay-legacy"
                     :open   showing
                     :popper    popper}
      children]]))

(defnc MiniIconButton [{:keys [icon on-click class style size]}]
  [:button {:class ["btn btn-link btn-sm p-1" class]
            :style style
            :on-click on-click}
   (svg-icon icon (str "m-0 fill-current-color" " " (when size (str "nosco-icon--" size))))])

(defnc nbsp []
  "\u00a0")

(defnc DateFormatter [{:keys [date format] :or {format :short-datetime}}]
  [:span
   (if date (util/date-format date format) "–")])

(defnc Truncate [{:keys [length children] :or {length 30}}]
  (if-not (= (type children) js/String)
    (throw (ex-info "Truncate only accepts a string as a child." {:children children
                                                                  :true? (instance? js/String children)
                                                                  :type (type children)}))
    (goog.string/truncate children length false)))

;; TODO should handle images/iframes?
(defnc TruncateHTML [{:keys [length children] :or {length 60}}]
  (when-not (= (type children) js/String)
    (throw (ex-info "Truncate only accepts a string as a child." {:children children
                                                                  :true? (instance? js/String children)
                                                                  :type (type children)})))
  (let [container (js/document.createElement "div")]
    (goog.object/set container "innerHTML" children)
    (util/shorten-html-text container length)
    [nosco.ui.html/SafeHTMLStrict {:html (.-innerHTML container)}]))

(defnc InternalLink [{:keys [on-click route-name route-data route-query children class target title]}]
  [:a (merge {:class class
              :href (router/href route-name route-data route-query)
              :title title}
             (when on-click
               {:on-click on-click})
             (when target
               {:target target})) children])

(defnc InternalLinkButton [{:keys [route-name route-data icon title class]}]
  [:a {:class ["btn" class]
       :href (router/href route-name route-data)}
   (when icon (svg-icon icon)) title])

(defnc ButtonPanelRow [{:keys [children class icon] :as props}]
  (let [props' (dissoc props :children :class :className)
        merged-props (merge {:class ["dropdown-item" class]
                             :type "button"}
                            props')]
    [:button merged-props
     (when icon (svg-icon icon))
     children]))

(defnc PanelDivider [{:keys [class]}]
  [:div {:class ["dropdown-divider" class]}])

(defnc PanelHeader [{:keys [class children]}]
  [:div {:class ["dropdown-header" class]}
   children])

(defnc Panel [{:keys [class children]}]
  [:div {:class "dropdown-menu d-block p-static"}
   children])

(defnc PanelInternalLink [{:keys [route-name route-data class children target]}]
  [:a {:class ["dropdown-item" class]
       :target target
       :href (router/href route-name route-data)}
   [:<> children]])

(defnc SearchablePaginatedPanel [{:keys [children show-more-clicked loading? show-more? search-updated class-override style]}]
  [:div {:class (if class-override class-override "dropdown-menu d-block")
         :style style}
   [:<>
    [SearchInputBox {:on-change-fn search-updated
                     :class "mx-2 mb-2"}]
    children
    (when show-more?
      [ShowMoreButton {:class "w-75"
                       :on-click show-more-clicked
                       :is-loading? loading?}])]])

;; component that changes height and width
;; useful to test dropdown / popper logic
(defnc Bouncer []
  (let [[t set-t] (useState 0)
        height    (+ 50 (* (js/Math.sin (* t 1)) 10))
        width     (+ 50 (* (js/Math.cos (* t 1)) 10))]
    (useEffect (fn []
                 (let [timer (js/setInterval (fn []
                                               (js/console.log "increasing t")
                                               (set-t inc))
                                             1000)]
                   (fn []
                     (js/console.log "clear timeout")
                     (js/clearInterval timer))))
               [])
    [:div {:style {:height     (str height "px")
                   :width      (str width "px")
                   :background "yellow"}}]))

(defn- element-scrolled-to-bottom? [el]
  ;; adding 50 pixels of "buffer" for the detection to account
  ;; for 1. rounding issues with scrollTop 2. scrollHeight not
  ;; including margin, border, horizontal scrollbar
  ;; so this will return true if the scroll is 50 pixels from
  ;; the bottom, which works just fine for an infinite scroller
  (>= (+ (.-scrollTop el) 50)
      (- (.-scrollHeight el)
         (.-offsetHeight el))))

(defn- scroll-to-bottom! [el] (set! (.-scrollTop el) (.-scrollHeight el)))

(defnc InfiniteScrollContainer
  "A div element which notifies the parent when the user has scrolled to
  the bottom of the element. Passing `scroll-to-bottom` as some truthy value will
  cause the container to scroll to the bottom, i.e. to show a 'Loading more' indicator."
  [{:keys [scroll-to-bottom on-scroll-to-bottom children] :as props}]
  (let [container-ref (React/useRef)]
    (useEffect
     (fn []
       (when scroll-to-bottom
         (scroll-to-bottom! (.-current container-ref))))
     [scroll-to-bottom])
    [:div
     (-> {:ref container-ref
          :on-scroll
          (util/use-debounced-event-handler
           50
           (fn [event]
             (when (and on-scroll-to-bottom (element-scrolled-to-bottom? (.-target event)))
               (on-scroll-to-bottom))))}
         (nosco.react/merge-props (dissoc props :scroll-to-bottom :on-scroll-to-bottom)))
     children]))

(defnc Checkbox
  "Wraps a Bootstrap custom checkbox. Handles generating an ID for the
  input and label.

  Props:

  - :container-props - Props merged with the outer container element
  - :label-props - Props merged with the label element
  - :default-indeterminate - Like defaultChecked, but sets the state to indeterminate

  All other props are merged with the input element.

  Children are rendered within the label element."
  [{:keys [container-props label-props default-indeterminate children] :as props}]
  (let [input-id (.-current (React/useRef (str (random-uuid))))
        input-ref (React/useRef)]
    (useLayoutEffect
     (fn []
       (set! (.-current.indeterminate input-ref) default-indeterminate)
       (fn []))
     [])
    [:div
     (-> {:class ["custom-control" "custom-checkbox"]}
         (nosco.react/merge-props container-props))
     [:input
      (-> {:ref input-ref
           :id input-id
           :class ["custom-control-input"]
           :type "checkbox"}
          (nosco.react/merge-props (dissoc props
                                           :container-props
                                           :label-props
                                           :default-indeterminate)))]
     [:label
      (-> {:class ["custom-control-label" "w-100"]
           :for input-id}
          (nosco.react/merge-props label-props))
      children]]))

(defnc IconSearchInput
  "An input component which displays an icon on the right.

  Props:

  - :icon - The name of the icon passed to `nosco.icons/Icon`
  - :loading - When truthy, shows a loading spinner in place of the icon
  - :container-props - Props passed to the container div"
  [{:keys [icon loading container-props] :or {icon :icon-search} :as props} ref]
  [:div
   (-> {:class ["position-relative" "d-flex" "align-items-center"]}
       (nosco.react/merge-props container-props))
   [:input
    (-> {:ref ref
         :class ["pr-5" "form-control" "rounded-pill"]
         :type "text"}
        (nosco.react/merge-props (dissoc props :icon :loading :container-props)))]
   (if loading
     [:div
      {:class ["position-absolute" "spinner-border" "spinner-border-sm" "text-secondary"]
       :style {:right "1rem" :pointer-events "none"}}]
     [icons/Icon
      {:icon icon
       :class ["position-absolute" "mr-3"]
       :style {:right 0 :pointer-events "none"}}])])

(nosco.react/forward-ref! IconSearchInput)

(def StaticImageRootContext (React/createContext nil))

(defn use-static-image [src]
  (let [root-context (str (useContext StaticImageRootContext))
        base-uri (str root-context (when-not (str/ends-with? root-context "/") "/"))
        src (if (str/starts-with? src "/") (subs src 1) src)
        src (str base-uri src)]
    src))

(defnc StaticImage [{:keys [src] :as props}]
  [:img (nosco.react/merge-props
         props
         {:src (use-static-image src)})])

(defnc PoweredByNosco []
  [:a {:href "https://nos.co/idea-platform"
       :target "_blank"
       :class "nosco-powered-by-nosco"}
   [text/Text {:variant text/Body-Sm-Regular
               :class ["text-neutral-400"]}
    (t/t "powered by")]
   [:div {:class "nosco-powered-by-nosco__logo"}
    [:img {:src "/img/nosco-logo.svg"
           :width 131
           :height 32}]]])

(defnc ImageThumbWithFallback [{:keys [src width height border-radius] :as props
                                :or {border-radius :md}}]
  [:div {:style {:display "flex"
                 :flex-shrink "0"
                 :border-radius (case border-radius
                                  :sm "2px"
                                  :md "4px"
                                  :lg "6px")
                 :box-sizing "content-box"
                 :width width
                 :height height
                 :overflow "hidden"
                 :border (str "1px solid " colors/neutral-100)}}
   (if src
     [:img (nosco.react/merge-props {:src (images/thumbnail-url src)
                                     :style {:width "100%"
                                             :height "100%"
                                             :object-fit "cover"}}
                                    (dissoc props :src :width :height :border-radius))]
     [icons2/PlaceholderImage {:variant :channel
                               :style {:width "100%"
                                       :height "100%"
                                       :object-fit "cover"}}])])

(defnc Deadline [{:keys [deadline size]}]
  (let [now (js/Date.)]
    [pills/Pill (cond-> {:color (cond (util/deadline-over? deadline now)
                                      :error
                                      (util/deadline-close? deadline now)
                                      :warning
                                      :else
                                      :info)
                         :icon icons2/Alarm-small
                         :label (util/date-format deadline :medium-datetime)}
                  size (assoc :size size))]))

(defnc LiveDeadline [{:keys [deadline static]}]
  (let [deadline (hooks/useMemo #(js/Date. deadline) [deadline])
        [now ->now] (hooks/useState (js/Date.))
        deadline-close (util/deadline-close? deadline now)
        deadline-over (util/deadline-over? deadline now)
        relative (when (and (not static) deadline-close) (nosco-format/date-relative deadline now))]
    (hooks/useEffect
     (fn []
       (when relative
         (let [interval-id (js/setInterval
                            (fn [] (->now (js/Date.)))
                            (nosco-format/relative-date-refresh-interval relative))]
           (fn []
             (js/clearInterval interval-id)))))
     [relative])
    [:div
     {:class ["ideabox-about-deadline"
              (when (or deadline-close deadline-over) "ideabox-about-deadline--alarm")]}
     [:span {:class ["mr-spacing-4"]}
      [icons2/Icon {:icon icons2/Alarm-small}]]
     (cond
       relative
       (t/t "Closing %s" (nosco-format/format-date-relative relative))

       deadline-over
       (t/t "Closed on %s" (nosco-format/date-format deadline :medium-datetime))

       :else
       (t/t "Closing on %s" (nosco-format/date-format deadline :medium-datetime)))]))

(defnc Divider []
  [:span {:class "nosco-toolbar-separator"}])

(defnc SectionDivider []
  [:div {:class "nosco-section-divider"}])
